/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { Fragment } from 'react';
import { Button } from 'reactstrap';
import { Breadcrumb } from '../../types/surveyScope';
import { ViewMap } from '@constants/viewOptions';
import { ViewValues } from '@g17eco/types/surveyOverview';
import SortButton from './SortButton';
import { SortType } from '@utils/sort';
import { CardGridViewModeButtons } from './CardGridViewModeButtons';
import './styles.scss';
import { SimpleTooltip } from '@g17eco/molecules/simple-tooltip';

interface SurveySettingsToolbarProps {
  view: ViewValues;
  breadcrumbs: Breadcrumb[],
  handleDrillback: (breadcrumbs: Breadcrumb[]) => void,
  handleSort: (code: SortType) => void,
  children?: JSX.Element | JSX.Element[];
  currentSort: SortType,
  filterText?: string,
  setFilterText?: (text: string) => void;
  questionCount?: number;
}

const getRootTitle = (key: ViewValues) => ViewMap[key]?.label ?? 'Assigned Metrics';

const renderBreadcrumb = (text: string, onClick: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void, isLeaf: boolean) => {
  return <>
    <div><i className='fa fa-caret-right mx-2' /></div>
    <div className='survey-settings-breadcrumb'>
      <SimpleTooltip text={text}>
        <Button color='link' onClick={onClick} className={isLeaf ? 'leaf' : ''}>{text}</Button>
      </SimpleTooltip>
    </div>
  </>
};

export default function SurveySettingsToolbar(props: SurveySettingsToolbarProps) {
  const {
    view,
    breadcrumbs,
    handleDrillback,
    handleSort,
    currentSort,
  } = props;

  const handleBreadcrumbClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, index: number) => {
    e.preventDefault();
    handleDrillback(breadcrumbs.slice(0, index));
  };
  const currentView = breadcrumbs[breadcrumbs.length - 1];
  const isQuestionList = currentView && [ViewValues.QuestionList, ViewValues.StandardsQuestionList].includes(currentView.cardGroup);
  return (
    <div className='survey-settings-toolbar-container'>
      <div className='survey-settings-toolbar d-flex justify-content-end align-items-center'>
        {props.children}
        <div className='ms-2'>
          <CardGridViewModeButtons />
        </div>
        <span className='ms-2'>
          {isQuestionList ? null : <SortButton currentSort={currentSort} handleSort={handleSort} />}
        </span>
      </div>
      <div className='mt-1 survey-settings-toolbar-breadcrumbs d-flex'>
        {breadcrumbs.length > 0 ? renderBreadcrumb(getRootTitle(view), (e) => handleBreadcrumbClick(e, 0), breadcrumbs.length === 0) : null}
        {breadcrumbs && breadcrumbs.map((breadcrumb, i) => (
          <Fragment key={`breadcrumb-${breadcrumb.cardGroup}-${i}`}>
            {renderBreadcrumb(breadcrumb.title, (e) => handleBreadcrumbClick(e, i + 1), (i + 1) === breadcrumbs.length)}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
